// EventoCertificacion.js — Pestaña «Certificación del evento» del dashboard público.
//
// Los datos vienen de la plataforma de eventos (dashboards[].evento.event_id): son
// los inscritos del evento en vivo, no los usuarios de Mentoria. Por cada
// certificado (módulo) dice quién lo obtuvo y cómo cumplió cada sesión: en vivo,
// viendo la memoria o aprobando el examen.
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  nf, pct, bajarCSV,
  Heading, Eyebrow, H2, Note, Actions, Btn, Ghost, Input, Select,
  Panel, PanelHead, PanelBody, KpiGrid, KpiCard, Bars, BarRow, BarVal, Legend,
  Chips, Chip, TableWrap, Table, Th, Td, TableTools, Badge, Pager, Empty, Err,
} from './dashboardUI';

const COMO = {
  vivo:    { label: 'En vivo',          color: t => t.serieA },
  memoria: { label: 'Memoria',          color: t => t.serieC },
  examen:  { label: 'Examen aprobado',  color: t => t.lime },
  no:      { label: 'No asistió',       color: t => t.track },
};
const ORDEN_COMO = ['vivo', 'memoria', 'examen', 'no'];
const POR_PAGINA = 50;

// Una casilla por sesión del módulo: el color dice cómo la cumplió.
const Tira = styled.span`display:inline-flex;gap:3px;vertical-align:middle;`;
const Casilla = styled.i`
  width:14px;height:14px;border-radius:4px;display:inline-block;background:${p => p.$c};
  border:1px solid ${p => p.theme.line};
`;
const Pila = styled.div`
  display:flex;height:12px;border-radius:99px;overflow:hidden;background:${p => p.theme.track};
  i{display:block;height:100%;}
`;
const Filtros = styled.div`
  display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin-bottom:12px;
  input{min-width:200px;flex:1 1 220px;max-width:340px;width:auto;}
  select{flex:0 1 240px;width:auto;min-width:0;max-width:100%;}
`;

const norm = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export default function EventoCertificacion({ evento, tema, nombreArchivo, paisGlobal = '' }) {
  const certs = useMemo(() => evento?.certificados || [], [evento]);
  const [certSel, setCertSel] = useState('');        // '' = todos los módulos
  const [estado, setEstado] = useState('');          // '' | 'si' | 'no'
  const [q, setQ] = useState('');
  const [pais, setPais] = useState('');
  const [pagina, setPagina] = useState(0);

  const usuarios = useMemo(() => evento?.usuarios || [], [evento]);
  const paises = useMemo(() => [...new Set(usuarios.map(u => u.pais).filter(Boolean))].sort(), [usuarios]);
  const paisActivo = pais || paisGlobal;

  const visiblesCerts = certSel ? certs.filter(c => String(c.id) === certSel) : certs;

  // Un inscrito «se certificó» si tiene todos los módulos elegidos (o el elegido).
  const certificado = (u) => visiblesCerts.length > 0 && visiblesCerts.every(c => u.certs?.[c.id]?.certificado);

  const base = useMemo(() => usuarios.filter(u => {
    if (paisActivo && norm(u.pais) !== norm(paisActivo)) return false;
    if (q) {
      const t = norm(q);
      if (!norm(u.nombre).includes(t) && !norm(u.email).includes(t)) return false;
    }
    return true;
  }), [usuarios, paisActivo, q]);

  const filtrados = base.filter(u => (estado === 'si' ? certificado(u) : estado === 'no' ? !certificado(u) : true));
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const pag = Math.min(pagina, totalPaginas - 1);

  // Cifras por módulo sobre los inscritos del filtro (país / búsqueda).
  const resumen = useMemo(() => certs.map(c => {
    const conDato = base.filter(u => u.certs?.[c.id]);
    const si = conDato.filter(u => u.certs[c.id].certificado).length;
    const desc = conDato.filter(u => u.certs[c.id].descargado).length;
    const sesiones = (c.sesiones || []).map(s => {
      const cuenta = { vivo: 0, memoria: 0, examen: 0, no: 0 };
      conDato.forEach(u => { cuenta[u.certs[c.id].sesiones?.[s.id] || 'no'] += 1; });
      return { ...s, cuenta, total: conDato.length };
    });
    return { ...c, total: conDato.length, si, desc, sesiones };
  }), [certs, base]);

  if (evento?.error) return <Err>{evento.error}. Intenta de nuevo en unos minutos.</Err>;
  if (!certs.length) return <Empty>El evento no tiene certificados activos.</Empty>;

  const reglaTexto = (c) => {
    const n = (c.sesiones || []).length;
    const formas = ['en vivo'];
    if (c.memoria_cuenta) formas.push(`viendo la memoria (mínimo ${c.memoria_min_min} min)`);
    if (c.examen_cuenta) formas.push('aprobando su examen');
    const minimo = c.asistencia_min_pct != null ? Math.ceil((c.asistencia_min_pct / 100) * n) : n;
    return `${c.name}: ${minimo} de ${n} sesiones (${c.asistencia_min_pct ?? 100}%). Una sesión cuenta si la hizo ${formas.join(', ').replace(/, ([^,]*)$/, ' o $1')}.`;
  };

  const exportar = () => {
    const cols = ['Nombre', 'Correo', 'País', 'Especialidad', 'Ciudad', 'Fecha de registro'];
    certs.forEach(c => {
      cols.push(`${c.name}: certificado`, `${c.name}: descargado`, `${c.name}: sesiones cumplidas`);
      (c.sesiones || []).forEach(s => cols.push(`${c.name}: ${s.nombre}`));
    });
    const filas = filtrados.map(u => {
      const r = [u.nombre, u.email, u.pais, u.especialidad, u.ciudad, u.registro];
      certs.forEach(c => {
        const x = u.certs?.[c.id];
        r.push(x?.certificado ? 'Sí' : 'No', x?.descargado || 'No', x ? `${x.asistidas ?? 0} de ${(c.sesiones || []).length}` : '');
        (c.sesiones || []).forEach(s => r.push(COMO[x?.sesiones?.[s.id] || 'no'].label));
      });
      return r;
    });
    bajarCSV(nombreArchivo('certificacion_evento'), cols, filas);
  };

  return (
    <>
      <Heading>
        <div><Eyebrow>Certificación del evento</Eyebrow><H2>{evento.evento?.nombre || 'Evento en vivo'}</H2></div>
        <Actions><Btn onClick={exportar}>⬇ Descargar certificación</Btn></Actions>
      </Heading>
      <Note style={{ marginBottom: 12, maxWidth: 'none' }}>
        Inscritos al evento en vivo y quién obtuvo cada certificado. {resumen.map(reglaTexto).join(' ')}
      </Note>

      <KpiGrid>
        <KpiCard $tone={tema.serieA}>
          <span>Inscritos</span><strong>{nf.format(base.length)}</strong>
          <small>{paisActivo ? paisActivo : 'todos los países'}</small>
        </KpiCard>
        {resumen.map(c => (
          <KpiCard key={c.id} $tone={tema.ok}>
            <span>{c.name}</span><strong>{nf.format(c.si)}</strong>
            <small>certificados ({pct(c.si / Math.max(1, c.total))}) · {nf.format(c.total - c.si)} sin certificar · {nf.format(c.desc)} lo descargaron</small>
          </KpiCard>
        ))}
      </KpiGrid>

      {resumen.map(c => (
        <Panel key={c.id}>
          <PanelHead>
            <h3>{c.name}: cómo cumplieron cada sesión</h3>
            <p>Sobre {nf.format(c.total)} inscritos.</p>
          </PanelHead>
          <PanelBody>
            <Bars>
              {c.sesiones.map(s => (
                <BarRow key={s.id}>
                  <span title={s.nombre}>{s.nombre}</span>
                  <Pila>
                      {ORDEN_COMO.map(k => s.cuenta[k] > 0 && (
                        <i key={k} title={`${COMO[k].label}: ${nf.format(s.cuenta[k])}`}
                           style={{ width: `${(s.cuenta[k] / Math.max(1, s.total)) * 100}%`, background: COMO[k].color(tema) }} />
                      ))}
                  </Pila>
                  <BarVal>{nf.format(s.total - s.cuenta.no)}<em>{pct((s.total - s.cuenta.no) / Math.max(1, s.total))}</em></BarVal>
                </BarRow>
              ))}
            </Bars>
            <Legend>
              {ORDEN_COMO.map(k => <span key={k}><i style={{ background: COMO[k].color(tema) }} />{COMO[k].label}</span>)}
            </Legend>
          </PanelBody>
        </Panel>
      ))}

      <Panel>
        <PanelHead><h3>Inscritos</h3><p>Cada casilla es una sesión del módulo; el color dice cómo la cumplió.</p></PanelHead>
        <PanelBody style={{ paddingTop: 6 }}>
          <Filtros>
            <Input placeholder="Buscar por nombre o correo" value={q} onChange={e => { setQ(e.target.value); setPagina(0); }} />
            <Select value={pais} onChange={e => { setPais(e.target.value); setPagina(0); }} aria-label="País">
              <option value="">{paisGlobal ? `País del filtro (${paisGlobal})` : 'Todos los países'}</option>
              {paises.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
            {certs.length > 1 && (
              <Select value={certSel} onChange={e => { setCertSel(e.target.value); setPagina(0); }} aria-label="Certificado">
                <option value="">Todos los módulos</option>
                {certs.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </Select>
            )}
            <Chips role="group" aria-label="Estado">
              {[['', 'Todos'], ['si', 'Certificados'], ['no', 'Sin certificar']].map(([v, t]) => (
                <Chip key={v} $active={estado === v} onClick={() => { setEstado(v); setPagina(0); }}>{t}</Chip>
              ))}
            </Chips>
          </Filtros>
          <TableTools>
            <span>
              {nf.format(filtrados.length)} inscritos
              {certSel || certs.length === 1 ? '' : estado ? ' (certificado = todos los módulos)' : ''}
            </span>
            {(q || pais || estado || certSel) && (
              <Ghost onClick={() => { setQ(''); setPais(''); setEstado(''); setCertSel(''); setPagina(0); }}>Quitar filtros</Ghost>
            )}
          </TableTools>
          <TableWrap $max="640px">
            <Table>
              <thead>
                <tr>
                  <Th>Inscrito</Th>
                  <Th>País</Th>
                  <Th>Especialidad</Th>
                  {visiblesCerts.map(c => <Th key={c.id}>{c.name}</Th>)}
                </tr>
              </thead>
              <tbody>
                {filtrados.slice(pag * POR_PAGINA, (pag + 1) * POR_PAGINA).map(u => (
                  <tr key={u.email}>
                    <Td>
                      <div style={{ color: tema.text, fontWeight: 600 }}>{u.nombre || '—'}</div>
                      <small style={{ color: tema.muted }}>{u.email}</small>
                    </Td>
                    <Td>{u.pais || '—'}</Td>
                    <Td>{u.especialidad || '—'}</Td>
                    {visiblesCerts.map(c => {
                      const x = u.certs?.[c.id];
                      if (!x) return <Td key={c.id}>—</Td>;
                      return (
                        <Td key={c.id} style={{ whiteSpace: 'nowrap' }}>
                          <Badge $tone={x.certificado ? 'ok' : 'muted'}>{x.certificado ? 'Certificado' : 'No certificado'}</Badge>
                          <div style={{ marginTop: 5 }}>
                            <Tira>
                              {(c.sesiones || []).map(s => {
                                const como = x.sesiones?.[s.id] || 'no';
                                return <Casilla key={s.id} $c={COMO[como].color(tema)} title={`${s.nombre}: ${COMO[como].label}`} />;
                              })}
                            </Tira>
                            <small style={{ color: tema.muted, marginLeft: 6 }}>
                              {x.asistidas ?? 0} de {(c.sesiones || []).length}
                              {x.descargado ? ` · descargado ${x.descargado}` : ''}
                            </small>
                          </div>
                        </Td>
                      );
                    })}
                  </tr>
                ))}
                {filtrados.length === 0 && (
                  <tr><Td colSpan={3 + visiblesCerts.length} style={{ textAlign: 'center' }}>Ningún inscrito con estos filtros.</Td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
          {totalPaginas > 1 && (
            <Pager style={{ padding: '12px 0' }}>
              <Ghost disabled={pag === 0} onClick={() => setPagina(pag - 1)}>← Anterior</Ghost>
              <span>Página {pag + 1} de {totalPaginas}</span>
              <Ghost disabled={pag >= totalPaginas - 1} onClick={() => setPagina(pag + 1)}>Siguiente →</Ghost>
            </Pager>
          )}
        </PanelBody>
      </Panel>
    </>
  );
}
